import React, { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, User, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { PROGRAMS } from "../mock";
import { bookTrial } from "../api";
import Seo from "../components/Seo";

const CLASS_OPTIONS = ["Weekday 5 PM (Kids)", "Monday 6 PM (Little Warriors 4–9)", "Tue/Thu 6 PM (Kids, Teens & Adults)", "Weekday 7 PM (Adults)", "Mon/Wed 6 AM (Adults)", "Saturday morning", "Toddlers (Mon 4:15 / Wed 6:15)", "Not sure yet"];

const TrialBooking = () => {
  const { toast } = useToast();
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({ name: "", parent_name: "", student_age: "", email: "", phone: "", program: "", preferred_class: "", notes: "", sms_consent: false });
  const isMinor = ["Toddlers", "Kids", "Teens"].includes(form.program);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [done]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.phone || !form.program || !date) {
      toast({ title: "A few details missing", description: "Please add the student's name, a valid email, phone, a program, and pick a date." });
      return;
    }
    if (isMinor && !form.parent_name) {
      toast({ title: "Parent / guardian name needed", description: "For students under 18, please add the parent or guardian's name." });
      return;
    }
    setLoading(true);
    try {
      await bookTrial({
        ...form,
        preferred_date: date.toISOString().slice(0, 10),
        source: "trial_form",
      });
      setDone(true);
      toast({ title: "Trial requested! \ud83e\udd4b", description: "We'll confirm your free class shortly." });
    } catch (err) {
      console.error("Trial booking failed", err);
      toast({ title: "Something went wrong", description: "Please try again or give us a call." });
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="pt-24">
      <Seo
        title="Book a Free Brazilian Jiu-Jitsu Class in Alameda | Alameda BJJ"
        description="Book your free trial Brazilian Jiu-Jitsu class in Alameda, CA. Pick a day and program for toddlers, kids, teens or adults — no commitment, all levels welcome."
        path="/trial"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Free Trial", path: "/trial" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Free Trial</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            Book Your Free Class
          </h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Pick a day that works for you and tell us a little about who's training. Your first class is on us — no commitment.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {done ? (
            <div className="reveal max-w-lg mx-auto bg-white rounded-2xl border border-neutral-100 shadow-xl p-10 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-br-green/10 flex items-center justify-center mb-5">
                <CheckCircle2 className="text-br-green" size={32} />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#1A1A1A]">You're Booked!</h3>
              <p className="text-neutral-600 mt-3">
                Thanks, {form.name}. We've received your request for a free {form.program} trial on {date && date.toLocaleDateString()}. A confirmation email is on its way to {form.email}, and our team will reach out shortly to confirm your spot.
              </p>
              <Button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", program: "", notes: "" }); setDate(null); }} className="mt-7 bg-br-green hover:bg-[#00822f] text-white rounded-full px-8 h-12">
                Book Another
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-10">
              <div className="reveal">
                <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
                  <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold mb-4">
                    <CalendarDays size={18} className="text-br-green" /> Choose a date
                  </div>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < today}
                      className="rounded-xl border border-neutral-100"
                    />
                  </div>
                  {date && (
                    <p className="mt-4 text-sm text-center text-neutral-600">
                      Selected: <span className="font-semibold text-br-green">{date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="reveal">
                <form onSubmit={submit} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 sm:p-8 space-y-4">
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                    <Input value={form.name} onChange={change("name")} placeholder="Student name*" data-testid="trial-name" className="pl-11 h-12 rounded-xl border-neutral-300" />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                    <Input type="email" value={form.email} onChange={change("email")} placeholder="Email*" data-testid="trial-email" className="pl-11 h-12 rounded-xl border-neutral-300" />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" size={17} />
                    <Input type="tel" value={form.phone} onChange={change("phone")} placeholder="Mobile phone*" data-testid="trial-phone" className="pl-11 h-12 rounded-xl border-neutral-300" />
                  </div>
                  <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                    <SelectTrigger className="h-12 rounded-xl border-neutral-300" data-testid="trial-program">
                      <SelectValue placeholder="Which program?*" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAMS.map((p) => (
                        <SelectItem key={p.id} value={p.title}>{p.title} — {p.ages}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isMinor && (
                    <Input value={form.parent_name} onChange={change("parent_name")} placeholder="Parent / guardian name*" data-testid="trial-parent" className="h-12 rounded-xl border-neutral-300" />
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={form.student_age} onChange={change("student_age")} placeholder="Student age" data-testid="trial-age" className="h-12 rounded-xl border-neutral-300" />
                    <Select value={form.preferred_class} onValueChange={(v) => setForm({ ...form, preferred_class: v })}>
                      <SelectTrigger className="h-12 rounded-xl border-neutral-300" data-testid="trial-class"><SelectValue placeholder="Preferred class" /></SelectTrigger>
                      <SelectContent>
                        {CLASS_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={form.notes} onChange={change("notes")} placeholder="Anything we should know? (experience, goals, questions)" className="min-h-[90px] rounded-xl border-neutral-300" />
                  <label className="flex items-start gap-3 text-xs text-neutral-600 leading-snug cursor-pointer">
                    <Checkbox checked={form.sms_consent} onCheckedChange={(v) => setForm({ ...form, sms_consent: v === true })} data-testid="trial-sms-consent" className="mt-0.5" />
                    <span>Text me a reminder the day before my class (address, time & what to wear). Msg & data rates may apply. Reply STOP to opt out.</span>
                  </label>
                  <Button type="submit" disabled={loading} data-testid="trial-submit" className="w-full h-12 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base font-semibold disabled:opacity-70">
                    {loading ? "Booking..." : "Request Free Trial"}
                  </Button>
                  <p className="text-xs text-neutral-400 text-center">We'll confirm your spot by email or phone.</p>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrialBooking;
